import { observer } from 'mobx-react';
import { Input, InputType, InputDropdown, Button } from '../../../../core/components/Index';
import ApplyViewModel from './ApplyViewModel';
import useAsyncEffect from 'use-async-effect';
import { Steps } from './ApplyModels';
import { useParams } from 'react-router-dom';
import { Appl } from '../../../../core/Appl';
import Helper from '../../../../core/services/Helper';

const ContactInfo = observer(() => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');
  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.ContactInformation, memberId!);
  }, []);

  return (
    <div className="space-y-6 max-w-4xl">
      {/* vm.model.personalInformation.email */}
      <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Your Information</h1>
      {vm.contactInfoEditable && <><p className="text-black text-15px tracking--1percent leading-26px mb-4">
        SCERS uses your contact information to send important updates about your retirement application and benefits. Your email address is critical, as it will be used to send your Online Retirement Application for digital signature via DocuSign. Please ensure you enter it correctly.
      </p>
        <ul className="list-disc pl-6 text-black text-15px tracking--1percent leading-26px">
          <li><b>Email Address:</b> Enter and confirm your personal email to ensure accuracy. This is where you will receive your DocuSign documents.</li>
          <li><b>Primary Phone Number:</b> Provide the best number where SCERS can contact you. This number will appear on your application.</li>
          <li><b>Additional Phone Number (Optional):</b> If you have another number, click "+ Add Additional Phone Number" to include it. Additional numbers are for SCERS' reference only and will not appear on your application.</li>
        </ul>
        <p className='text-black text-15px tracking--1percent leading-26px mt-4'>Once complete, click Save & Continue to move to the next step.</p>
        <p className="text-gray-700 font-medium text-22px">Contact Information</p>
        <div className="space-y-6 mt-6 max-w-[500px]">
          <div>
            <Input
              id="email"
              label="Email Address"
              value={vm.model.personalInformation.email}
              inputType={InputType.Email}
              onChange={(e) => vm.onInputChange(e, 'email', vm.model.personalInformation)}
              required={false}
            />
          </div>
          <div>
            <Input
              id="confirmEmail"
              label="Confirm Email Address"
              value={vm.model.personalInformation.confirmEmail}
              inputType={InputType.Email}
              onChange={(e) => vm.onInputChange(e, 'confirmEmail', vm.model.personalInformation)}
              required={false}
            />
          </div>
          <div className="flex flex-wrap gap-4 items-center w-full">
            <div className="md:flex-1 md:max-w-[400px] w-full">
              <Input
                id="phone"
                label="Phone Number (Primary)"
                value={vm.model.personalInformation.phone}
                inputType={InputType.Phone}
                onChange={(e) => vm.onInputChange(e, 'phone', vm.model.personalInformation)}
                required={false}
              />
            </div>
            <div className="md:flex-1 md:min-w-[240px]  w-full">
              <InputDropdown
                id="phoneType"
                value={vm.model.personalInformation.phoneType}
                label="Phone Type"
                placeholder="Select"
                options={vm.model.phoneTypeListItems}
                optionValue="key"
                optionLabel="value"
                required={false}
                style={{ width: '150px' }}
                onChange={(e) => {
                  vm.onInputChange(e, 'phoneType', vm.model.personalInformation);
                }}
              />
            </div>
          </div>
          {!vm.alternativeNumberEditable && (
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 max-w-[400px]">
                <button onClick={async () => await vm.setAlternativeNumberEditable(true)} className="text-primary-500 text-sm underline">
                  + Add Additional Phone Number
                </button>
              </div>
            </div>
          )}
          {vm.alternativeNumberEditable && (
            <div className="flex flex-wrap gap-4 items-center">
              <div className="md:flex-1 md:max-w-[400px] w-full" >
                <Input
                  id="alternatePhone"
                  value={vm.model.personalInformation.alternatePhone}
                  label="Phone Number (Alt)"
                  inputType={InputType.Phone}
                  onChange={(e) => vm.onInputChange(e, 'alternatePhone', vm.model.personalInformation)}
                  required={false}
                />
              </div>
              <div className="md:flex-1 md:min-w-[240px] w-full">
                <div className='flex flex-row'>
                  <div>
                    <InputDropdown
                      id="alternatePhoneType"
                      label="Phone Type"
                      value={vm.model.personalInformation.alternatePhoneType}
                      placeholder="Select"
                      options={vm.model.phoneTypeListItems}
                      optionValue="key"
                      optionLabel="value"
                      required={false}
                      style={{ width: '150px' }}
                      onChange={(e) => {
                        vm.onInputChange(e, 'alternatePhoneType', vm.model.personalInformation);
                      }}
                    />
                  </div>
                  <div
                    style={{ cursor: 'pointer', marginTop: '25px' }}
                    onClick={() => { vm.setAlternativeNumberEditable(false) }}
                  >
                    <svg
                      width="14"
                      height="16"
                      viewBox="0 0 14 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="ml-2 mt-3 w-4 h-4 transition-colors duration-200"
                      style={{ transition: "fill 0.2s ease-in-out" }}
                      onMouseEnter={(e) => (e.currentTarget.querySelector('path')!.style.fill = "red")}
                      onMouseLeave={(e) => (e.currentTarget.querySelector('path')!.style.fill = "#949494")}
                    >
                      <path d="M5.11875 0H8.88125C9.25938 0 9.60625 0.2125 9.775 0.553125L10 1H13C13.5531 1 14 1.44687 14 2C14 2.55312 13.5531 3 13 3H1C0.446875 3 0 2.55312 0 2C0 1.44687 0.446875 1 1 1H4L4.225 0.553125C4.39375 0.2125 4.74062 0 5.11875 0ZM1 4H13L12.3375 14.5938C12.2875 15.3844 11.6312 16 10.8406 16H3.15937C2.36875 16 1.7125 15.3844 1.6625 14.5938L1 4ZM4.46875 7.46875C4.175 7.7625 4.175 8.2375 4.46875 8.52812L5.9375 9.99687L4.46875 11.4656C4.175 11.7594 4.175 12.2344 4.46875 12.525C4.7625 12.8156 5.2375 12.8187 5.52812 12.525L6.99687 11.0562L8.46562 12.525C8.75937 12.8187 9.23438 12.8187 9.525 12.525C9.81562 12.2312 9.81875 11.7562 9.525 11.4656L8.05625 9.99687L9.525 8.52812C9.81875 8.23438 9.81875 7.75938 9.525 7.46875C9.23125 7.17812 8.75625 7.175 8.46562 7.46875L6.99687 8.9375L5.52812 7.46875C5.23438 7.175 4.75938 7.175 4.46875 7.46875Z"
                        fill="#949494"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex mt-6 flex-wrap gap-2 sm:flex-nowrap">
          <Button title="Save & Continue" rounded
            onClick={async () => await vm.onContactInformationStepChangeAsync()}
            disabled={
              vm.model.personalInformation.email === ""
              || vm.model.personalInformation.confirmEmail === ""
              || vm.model.personalInformation.phone === ""
              || vm.model.personalInformation.phoneType === ""
              || (vm.alternativeNumberEditable && vm.model.personalInformation.alternatePhone === "")
              || (vm.alternativeNumberEditable && vm.model.personalInformation.alternatePhoneType === "")
            }
          />

          {vm.navigatedFromReviewScreen && <Button title="Cancel & Return to Review" severity='secondary' rounded
            onClick={async () => await vm.navigateToReviewStepAsync()}
          />}
        </div>
      </>
      }
      {!vm.contactInfoEditable && <><div>
        <p className="text-black text-15px tracking--1percent leading-26px mb-4">
          Please review and confirm your contact details below. This information is crucial for processing your service retirement application and for future communications.
        </p>
        <p className="text-gray-700 font-medium text-22px mb-4">Contact Information</p>
        <p className="text-[18px] font-semibold mb-1">Email</p>
        <p className="text-15px font-normal mb-8 ml-3">{vm.model.personalInformation.email}</p>
        <p className="text-[18px] font-semibold mb-1">Primary Phone</p>
        <p className="text-15px font-normal mb-8 ml-3">{Helper.formatPhone(vm.model.personalInformation.phone!)} ({vm.model.personalInformation.phoneType})</p>
        <p className="text-[18px] font-semibold mb-1">Alternate Phone</p>
        {vm.model.personalInformation.alternatePhone &&
          <p className="text-15px font-normal mb-8 ml-3" >{Helper.formatPhone(vm.model.personalInformation.alternatePhone!)} ({vm.model.personalInformation.alternatePhoneType})</p>
        }
        {!vm.model.personalInformation.alternatePhone && <p className='text-gray-600 italic'>No alternate phone provided.</p>
        }

      </div>
        <div className="flex space-x-4 mt-6">
          {!vm.navigatedFromReviewScreen &&
            <Button title="Save & Continue" rounded
              onClick={async () => await vm.onContactInformationStepChangeAsync()}
            />
          }
          {vm.navigatedFromReviewScreen &&
            <Button title="Return to Review" rounded
              onClick={() => vm.navigateToReviewStepAsync()} />
          }
          <Button severity="warning" rounded
            onClick={() => vm.contactInfoEditable = true}
            label={`Make a Change`} outlined
          />
        </div>
      </>
      }
    </div >
  );
});

export default ContactInfo;
